import dayjs from 'dayjs'

import { isSelectionRangeUnavailable } from 'features/Home/Book/Booking/Schedule/PatientSchedule/ScheduleCalendar/scheduleAvailability'
import type { DoctorAvailabilityGroup } from 'features/Schedule/api/schedule.types'

type VisitInput = {
  availability: DoctorAvailabilityGroup[] | undefined
  // Null until the EMR providers load; the calendar checks nobody until then either.
  emrLinkedUserIds: Set<number> | null
  staffId: number | undefined
  time: Date | string | undefined
  duration: number
}

// Same rule as the calendar slot click: only EMR-linked doctors carry availability.
export const isDoctorAwayForVisit = ({
  availability,
  emrLinkedUserIds,
  staffId,
  time,
  duration,
}: VisitInput): boolean => {
  if (!staffId || !time || !emrLinkedUserIds?.has(staffId)) return false
  const start = dayjs(time)
  if (!start.isValid()) return false
  // No zone: form times are already site wall-clock digits.
  return isSelectionRangeUnavailable(
    availability,
    [staffId],
    start.toDate(),
    start.add(Math.max(duration, 5), 'minute').toDate(),
  )
}

type KeyInput = {
  staffId: number | undefined
  day: string
  time: Date | string | undefined
  duration: number
}

// Identifies the doctor and time a user already agreed to book while away.
export const doctorAwayKey = ({ staffId, day, time, duration }: KeyInput): string =>
  [staffId ?? '', day, time ? dayjs(time).format('HH:mm') : '', duration].join('|')

type FormVisit = {
  consultation_staff?: number
  date?: Date | string
  time?: Date | string
  duration?: number
}

// The doctor and time a form describes, on its own day or the dialog's.
export const awayVisitOf = (values: FormVisit, fallbackDay: string): KeyInput => ({
  staffId: values.consultation_staff,
  day: dayjs(values.date ?? fallbackDay).format('YYYY-MM-DD'),
  time: values.time,
  duration: values.duration ?? 0,
})
